// API de la outbox: encolar y cambiar el estado de las operaciones pendientes.
// El drainer (src/lib/sync/drainer.js) consume getPending() y marca cada fila
// segun la respuesta de POST /api/sync/batch.
import { outboxDb } from "./outboxDb";

const MAX_RETRIES = 5;

// Encola una mutacion. Genera el clientUuid (clave de idempotency) y lo mete
// DENTRO del payload, asi el backend lo ve aunque el sobre del batch se arme
// despues. Devuelve el clientUuid para que la UI marque su fila optimista.
//
// opts.clientUuid: si el caller ya lo genero (para taggear la fila optimista del
//   cache con el mismo uuid y poder limpiarla al descartar), se reusa.
// opts.queryKey: prefijo/clave de la lista donde se inserto la fila optimista.
//   Se guarda en la fila para que "descartar" pueda removerla del cache (Paso 8).
// opts.resourceId: id del recurso (server) a actualizar/eliminar. Requerido para
//   UPDATE/DELETE, null en CREATE.
export async function enqueue(entity, operation, payload, { clientUuid, queryKey, resourceId, establishmentId, removedRow } = {}) {
    const uuid = clientUuid ?? crypto.randomUUID();
    await outboxDb.outbox.add({
        clientUuid: uuid,
        entity,
        operation,
        resourceId: resourceId ?? null,
        payload: { ...payload, clientUuid: uuid },
        queryKey: queryKey ?? null,
        // Multi-establecimiento: a qué establecimiento pertenece la operación,
        // para drenarla con el token scoped correcto.
        establishmentId: establishmentId ?? null,
        // Solo para DELETE: la fila tal cual estaba en el cache antes de
        // sacarla, para poder reinsertarla si el server rechaza la operación
        // (ver revertOptimistic en drainer.js).
        removedRow: removedRow ?? null,
        status: "pending",
        retries: 0,
        createdAt: Date.now(),
    });
    return uuid;
}

// La operacion encolada de un clientUuid, o undefined si ya no esta (porque
// sincronizo: markSynced BORRA la fila). La usa saveOffline para editar o
// descartar un CREATE que todavia no salio, en vez de encolar un UPDATE/DELETE
// contra un id que el server todavia no conoce.
export async function getByClientUuid(clientUuid) {
    if (!clientUuid) return undefined;
    return outboxDb.outbox.where("clientUuid").equals(clientUuid).first();
}

// Operaciones cuya fila OPTIMISTA sigue viva en el cache de lectura, es decir
// todo lo que el server todavia no confirmo. Incluye `conflict` e `in_flight`,
// que getPending() deja afuera porque no hay que reenviarlas: para el cache
// igual cuentan, porque su fila se veria pisada por un refetch.
// (`synced` no aparece: markSynced BORRA la fila.)
export async function getUnsynced() {
    return outboxDb.outbox
        .where("status")
        .anyOf(["pending", "in_flight", "error", "conflict"])
        .toArray();
}

// Operaciones que todavia hay que mandar: pendientes + las que fallaron con
// error transitorio (se reintentan). Ordenadas FIFO por createdAt.
export async function getPending() {
    return outboxDb.outbox
        .where("status")
        .anyOf(["pending", "error"])
        .sortBy("createdAt");
}

export async function markInFlight(id) {
    await outboxDb.outbox.update(id, { status: "in_flight" });
}

// Operacion confirmada por el server: se ELIMINA de la outbox (poda inmediata).
// No conservamos las "synced": nada las lee despues y se acumularian sin limite
// en IndexedDB (un celu con uso diario juntaria miles). La idempotency vive en el
// server (clientUuid); no necesitamos el registro local una vez confirmado.
export async function markSynced(id) {
    await outboxDb.outbox.delete(id);
}

// Fallo TRANSITORIO de red/sesion (no es rechazo del dato): vuelve a "pending"
// SIN consumir reintentos. El batch siempre responde 200 salvo 401/403, asi que
// lo que cae en el catch del drainer es "sin red" o "sesion vencida" -> no hay
// que asustar al usuario con un "Error" rojo; se reintenta al volver la senal.
export async function markPending(id) {
    await outboxDb.outbox.update(id, { status: "pending", lastError: null });
}

// Poda del acumulado historico: borra filas ya confirmadas. markSynced ya borra
// en el acto, asi que esto solo limpia dispositivos que venian guardando "synced"
// de versiones anteriores. Se corre una vez al boot.
export async function pruneSynced() {
    await outboxDb.outbox.where("status").equals("synced").delete();
    // Podar el idMap (clientUuid→serverId): sus entradas solo sirven para resolver
    // hijos que se drenan poco después del padre; 7 días es holgado. Evita que la
    // tabla crezca sin límite en dispositivos con uso diario.
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    await outboxDb.idmap.where("createdAt").below(cutoff).delete();
}

// Error transitorio: vuelve a "pending" para reintentar, hasta MAX_RETRIES;
// pasado el limite queda "error" (visible para el usuario, no se reintenta solo).
export async function markError(id, error) {
    const row = await outboxDb.outbox.get(id);
    if (!row) return;
    await outboxDb.outbox.update(id, {
        status: row.retries >= MAX_RETRIES ? "error" : "pending",
        retries: row.retries + 1,
        lastError: error,
    });
}

// Rechazo deterministico (forbidden / bad_request / not_found): NO se reintenta
// solo, requiere intervencion humana. Guarda el errorCode para que el drawer y
// el drainer distingan forbidden (refrescar permisos) de not_found/bad_request.
export async function markConflict(id, error, errorCode) {
    await outboxDb.outbox.update(id, {
        status: "conflict",
        lastError: error,
        errorCode: errorCode ?? null,
    });
}

// Reintento MANUAL desde la UI (Paso 8): el usuario fuerza el reenvio de una
// fila en error (transitorio agotado) o conflict (rechazo del server). La
// vuelve a "pending" y resetea el contador para que el drainer la tome de nuevo.
export async function retry(id) {
    await outboxDb.outbox.update(id, {
        status: "pending",
        retries: 0,
        lastError: null,
    });
}

// Operaciones encoladas que DEPENDEN de uno o más CREATE todavía sin confirmar:
// las que llevan una FK temporal "pending-<clientUuid>" en su payload (una
// observación/pesaje de un animal creado sin conexión, por ejemplo).
//
// Sirve para descartar en cascada: si se descarta el CREATE del padre, sus hijos
// quedan apuntando a un uuid que NUNCA va a entrar al idmap, y el drainer los
// difiere en silencio en cada ciclo —para siempre— mientras la UI le sigue
// diciendo al usuario "se sincronizará automáticamente".
//
// Busca sobre el payload serializado en vez de recorrerlo campo por campo porque
// la FK aparece con muchas formas según la entidad (`animalId`, `animalIds:[…]`,
// `fathers:[{fatherId}]`, `motherId`…); el token `"pending-<uuid>"` con comillas
// es inequívoco. Misma convención que resolvePendingRefs en el drainer.
export async function getDependientesDe(clientUuids) {
    const uuids = [...new Set((clientUuids ?? []).filter(Boolean))];
    if (uuids.length === 0) return [];
    const rows = await outboxDb.outbox.toArray();
    return rows.filter((r) => {
        // `in_flight` ya salió al server: no se puede desandar. No debería pasar
        // (el drainer nunca manda una op con FK sin resolver), es defensivo.
        if (r.status === "in_flight") return false;
        const serializado = JSON.stringify(r.payload ?? {});
        return uuids.some((u) => serializado.includes(`"pending-${u}"`));
    });
}

// Descartar una fila (Paso 8): el usuario abandona el cambio. La borra de la
// outbox y devuelve la fila borrada para que el caller pueda limpiar su fila
// optimista del cache de lectura (via queryKey + clientUuid).
export async function discard(id) {
    const row = await outboxDb.outbox.get(id);
    await outboxDb.outbox.delete(id);
    return row;
}

// --- Mapa clientUuid -> serverId (cadena de dependencias offline) ---

// Registra el id real que el server asignó a un CREATE recién confirmado. El
// drainer lo usa para resolver las FK "pending-<clientUuid>" de los hijos.
// Persistente: el hijo puede drenarse en un ciclo/sesión posterior al del padre.
export async function recordServerId(clientUuid, serverId) {
    if (!clientUuid || serverId == null) return;
    await outboxDb.idmap.put({ clientUuid, serverId, createdAt: Date.now() });
}

// Todo el mapa como Map(clientUuid -> serverId), para resolver FKs en un ciclo
// de drenado sin una query por FK.
export async function getIdMap() {
    const rows = await outboxDb.idmap.toArray();
    return new Map(rows.map((r) => [r.clientUuid, r.serverId]));
}

// Reemplaza el payload de una fila del outbox (tras resolver sus FK temporales),
// para que la op quede autocontenida y no se vuelva a diferir en el próximo ciclo.
export async function updatePayload(id, payload) {
    await outboxDb.outbox.update(id, { payload });
}
